import { ComplianceIssue, ImageStatus, TranslationObject, Viewpoint } from '@monkvision/types';

/**
 * Translations available for image labels.
 */
export interface ImageLabels {
  /**
   * The title of the image status or compliance issue.
   */
  title: TranslationObject;
  /**
   * The description of the image status or compliance issue.
   */
  description: TranslationObject;
}

/**
 * Translation labels for the different image status.
 */
export const imageStatusLabels: Record<ImageStatus, ImageLabels> = {
  [ImageStatus.UPLOADING]: {
    title: {
      en: 'Uploading',
      fi: 'Lataa',
      cs: 'Nahrávání',
      no: 'Lasting opp',
      ro: 'Încărcare',
      pl: 'Wgrywanie',
      da: 'Uploader',
      sv: 'Laddar upp',
      es: 'Cargando',
      pt: 'Carregando',
      fr: 'Upload en cours',
      de: 'Hochladen',
      nl: 'Bezig met uploaden',
      it: 'Caricamento in corso',
    },
    description: {
      en: 'This image is being uploaded.',
      fi: 'Kuva ladataan.',
      cs: 'Obrázek se nahrává.',
      no: 'Bildet lastes opp',
      ro: 'Această fotografie este încărcată.',
      pl: 'Zdjęcie jest przesyłane.',
      da: 'Dette billede bliver uploadet.',
      sv: 'Den här bilden laddas upp.',
      es: 'Esta imagen se está cargando.',
      pt: 'Esta imagem está sendo carregada.',
      fr: "Upload de l'image en cours.",
      de: 'Dieses Bild wird gerade hochgeladen.',
      nl: 'Deze afbeelding wordt geüpload.',
      it: 'Questa immagine è in fase di caricamento.',
    },
  },
  [ImageStatus.COMPLIANCE_RUNNING]: {
    title: {
      en: 'Analyzing',
      fi: 'Päättele',
      cs: 'Analyzování',
      no: 'Analyserer',
      ro: 'Analizând',
      pl: 'Analiza',
      da: 'Analyserer',
      sv: 'Analyseras',
      es: 'Analizando',
      pt: 'Analisando',
      fr: 'Analyse',
      de: 'Analysieren Sie',
      nl: 'Analyseren',
      it: 'Analizzando',
    },
    description: {
      en: 'This image is being analyzed.',
      fi: 'Kuva analysoidaan.',
      cs: 'Obraz je analyzován.',
      no: 'Bildet analyseres',
      ro: 'Această fotografie este analizată.',
      pl: 'Zdjęcie jest analizowane.',
      da: 'Dette billede analyseres.',
      sv: 'Den här bilden analyseras.',
      es: 'Esta imagen se está analizando.',
      pt: 'Esta imagem está sendo analisada.',
      fr: "Cette image est en train d'être analysée.",
      de: 'Dieses Bild wird gerade analysiert.',
      nl: 'Deze afbeelding wordt geanalyseerd.',
      it: 'Questa immagine è in fase di analisi.',
    },
  },
  [ImageStatus.SUCCESS]: {
    title: {
      en: 'Success',
      fi: 'Onnistui',
      cs: 'Úspěch',
      no: 'Ferdig',
      ro: 'Succes',
      pl: 'Pomyślnie',
      da: 'Succes',
      sv: 'Lyckades',
      es: 'Éxito',
      pt: 'Sucesso',
      fr: 'Succès',
      de: 'Erfolg',
      nl: 'Succes',
      it: 'Successo',
    },
    description: {
      en: 'This image seems good, but you can still retake it if you want.',
      fi: 'Tämä kuva näyttää hyvältä, mutta voit ottaa sen uudelleen, jos haluat.',
      cs: 'Tento obrázek vypadá dobře, ale můžete jej stále zkusit znovu, pokud chcete.',
      no: 'Dette bildet ser bra ut, men du kan ta det på nytt hvis du ønsker det.',
      ro: 'Această fotografie pare bună, dar puteți să o refaceți dacă doriți.',
      pl: 'To zdjęcie jest dobre, ale możesz zrobić nowe, jeśli chcesz.',
      da: 'Dette billede ser godt ud, men du kan tage det om igen, hvis du vil.',
      sv: 'Den här bilden ser bra ut, men du kan ta ett nytt foto om du vill.',
      es: 'Esta imagen parece buena, pero puede volver a tomarla si lo desea.',
      pt: 'Esta imagem parece boa, mas você pode tirar outra foto se quiser.',
      fr: 'Cette image paraît bonne, mais vous pouvez quand-même la reprendre si besoin.',
      de: 'Dieses Bild scheint gut zu sein, aber Sie können es noch einmal aufnehmen, wenn Sie wollen.',
      nl: 'Dit beeld lijkt goed, maar je kunt het nog steeds opnieuw maken als je wilt.',
      it: 'Questa immagine sembra buona, ma puoi comunque rifarla se vuoi.',
    },
  },
  [ImageStatus.UPLOAD_FAILED]: {
    title: {
      en: 'Upload failed',
      fi: 'Lataus epäonnistui',
      cs: 'Nahrání selhalo',
      no: 'Fil opplasting mislyktes',
      ro: 'Încărcarea a eșuat',
      pl: 'Wgrywanie nie powiodło się',
      da: 'Upload mislykkedes',
      sv: 'Uppladdningen misslyckades',
      es: 'La carga falló',
      pt: 'Falha ao carregar',
      fr: 'Upload impossible',
      de: 'Upload fehlgeschlagen',
      nl: 'Upload mislukt',
      it: 'Caricamento non riuscito',
    },
    description: {
      en: 'Make sure you have a good connection and take the photo again.',
      fi: 'Varmista hyvä yhteys ja ota kuva uudelleen.',
      cs: 'Ujistěte se, že máte stabilní připojení a zopakujte snímání.',
      no: 'Sørg for god forbindelse og ta bildet igjen.',
      ro: 'Asigurați-vă că aveți o conexiune bună și faceți fotografia din nou.',
      pl: 'Upewnij się, że masz dobre połączenie i spróbuj zrobić zdjęcie ponownie.',
      da: 'Sørg for, at du har en god forbindelse, og tag billedet igen.',
      sv: 'Se till att du har en bra anslutning och ta fotot igen.',
      es: 'Asegúrese de tener una buena conexión e intente tomar la foto de nuevo.',
      pt: 'Certifique-se de que você tem uma boa conexão e tire a foto novamente.',
      fr: 'Vérifiez que vous avez une bonne connexion et reprenez la photo.',
      de: 'Vergewissern Sie sich, dass Sie eine gute Verbindung haben, und machen Sie das Foto erneut.',
      nl: 'Zorg ervoor dat je een goede verbinding hebt en neem de foto opnieuw.',
      it: 'Assicurati di avere una buona connessione e scatta di nuovo la foto.',
    },
  },
  [ImageStatus.UPLOAD_ERROR]: {
    title: {
      en: 'Upload error',
      fi: 'Latausvirhe',
      cs: 'Chyba při nahrávání',
      no: 'Filopplasting feilet',
      ro: 'Eroare la încărcare',
      pl: 'Błąd przesyłania',
      da: 'Uploadfejl',
      sv: 'Fel vid uppladdning',
      es: 'Error al cargar',
      pt: 'Erro ao carregar',
      fr: "Erreur d'upload",
      de: 'Upload-Fehler',
      nl: 'Fout bij uploaden',
      it: 'Errore di caricamento',
    },
    description: {
      en: 'An error occurred during the image upload.',
      fi: 'Virhe tapahtui kuvan latauksessa.',
      cs: 'Došlo k chybě při nahrávání obrázku.',
      no: 'En feil oppstod under opplastingen av bildet.',
      ro: 'A apărut o eroare în timpul încărcării fotografiei.',
      pl: 'Wystąpił błąd podczas przesyłania zdjęcia.',
      da: 'Der opstod en fejl under upload af billedet.',
      sv: 'Ett fel uppstod vid uppladdningen av bilden.',
      es: 'Se produjo un error al cargar la imagen.',
      pt: 'Ocorreu um erro durante o carregamento da imagem.',
      fr: "Une erreur s'est produite lors de l'upload de l'image.",
      de: 'Beim Hochladen des Bildes ist ein Fehler aufgetreten.',
      nl: 'Er is een fout opgetreden tijdens het uploaden van de afbeelding.',
      it: "Si è verificato un errore durante il caricamento dell'immagine.",
    },
  },
  [ImageStatus.NOT_COMPLIANT]: {
    title: {
      en: 'Error',
      fi: 'Virhe',
      cs: 'Chyba',
      no: 'Feil',
      ro: 'Eroare',
      pl: 'Błąd',
      da: 'Fejl',
      sv: 'Fel',
      es: 'Error',
      pt: 'Erro',
      fr: 'Erreur',
      de: 'Fehler',
      nl: 'Fout',
      it: 'Errore',
    },
    description: {
      en: 'Make sure the image quality is good and take the photo again.',
      fi: 'Varmista, että kuvan laatu on hyvä, ja ota kuva uudelleen.',
      cs: 'Ujistěte se, že kvalita obrazu je dobrá a pořiďte další snímek.',
      no: 'Sørg for at bildet er av god kvalitet og ta bildet igjen.',
      ro: 'Asigurați-vă că fotografia are o calitate bună și faceți fotografia din nou.',
      pl: 'Upewnij się, że jakość zdjęcia jest dobra, i zrób zdjęcie ponownie.',
      da: 'Sørg for, at billedkvaliteten er god, og tag billedet igen.',
      sv: 'Se till att bildkvaliteten är bra och ta fotot igen.',
      es: 'Asegúrese de que la calidad de la imagen sea buena y vuelva a tomar la foto.',
      pt: 'Certifique-se de que a qualidade da imagem esteja boa e tire a foto novamente.',
      fr: "Vérifiez que la qualité de l'image est bonne et reprenez la photo.",
      de: 'Vergewissern Sie sich, dass die Bildqualität gut ist, und machen Sie das Foto erneut.',
      nl: 'Zorg ervoor dat de kwaliteit van de afbeelding goed is en neem de foto opnieuw.',
      it: "Assicurati che la qualità dell'immagine sia buona e scatta di nuovo la foto.",
    },
  },
};

/**
 * Translation labels for the different compliance issues.
 */
export const complianceIssueLabels: Record<ComplianceIssue, ImageLabels> = {
  [ComplianceIssue.OTHER]: {
    title: {
      en: 'Not compliant',
      fi: 'Ei täytä vaatimuksia',
      cs: 'Neodpovídá požadavkům',
      no: 'Ikke i samsvar',
      ro: 'Neconform',
      pl: 'Niezgodny',
      da: 'Ikke godkendt',
      sv: 'Uppfyller inte kraven',
      es: 'No cumple',
      pt: 'Não está em conformidade',
      fr: 'Non valide',
      de: 'Nicht konform',
      nl: 'Niet-conform',
      it: 'Non conforme',
    },
    description: {
      en: 'Make sure that the image quality is good and that the vehicle is properly aligned with the guides.',
      fi: 'Varmista, että kuvan laatu on hyvä ja että ajoneuvo on oikein kohdistettu ohjaimiin.',
      cs: 'Ujistěte se, že kvalita obrazu je dobrá a že vozidlo je správně zarovnáno podle vodítek.',
      no: 'Sørg for at bildet er av god kvalitet og at kjøretøyet er riktig justert med retningslinjene.',
      ro: 'Asigurați-vă că fotografia are o calitate bună și că vehiculul este aliniat corect cu ghidajele.',
      pl: 'Upewnij się, że jakość zdjęcia jest dobra i że pojazd jest prawidłowo ustawiony względem linii prowadzących.',
      da: 'Sørg for, at billedkvaliteten er god, og at køretøjet er korrekt justeret i forhold til vejledningslinjerne.',
      sv: 'Se till att bildkvaliteten är bra och att fordonet är korrekt justerat efter linjerna.',
      es: 'Asegúrese de que la calidad de la imagen sea buena y de que el vehículo esté correctamente alineado con las guías.',
      pt: 'Certifique-se de que a qualidade da imagem esteja boa e que o veículo esteja alinhado corretamente com as guias.',
      fr: "Assurez-vous que la qualité de l'image est bonne et que le véhicule est bien aligné avec les guides.",
      de: 'Vergewissern Sie sich, dass die Bildqualität gut ist und dass das Fahrzeug richtig an den Führungslinien ausgerichtet ist.',
      nl: 'Zorg ervoor dat de kwaliteit van de afbeelding goed is en dat het voertuig correct is uitgelijnd met de gidsen.',
      it: "Assicurati che la qualità dell'immagine sia buona e che il veicolo sia correttamente allineato con le guide.",
    },
  },
  [ComplianceIssue.LOW_RESOLUTION]: {
    title: {
      en: 'Low resolution',
      fi: 'Korkea resoluutio',
      cs: 'Nízké rozlišení',
      no: 'Lav oppløsning',
      ro: 'Rezoluție scăzută',
      pl: 'Niska rozdzielczość',
      da: 'Lav opløsning',
      sv: 'Låg upplösning',
      es: 'Baja resolución',
      pt: 'Baixa resolução',
      fr: 'Basse résolution',
      de: 'Niedrige Auflösung',
      nl: 'Lage resolutie',
      it: 'Bassa risoluzione',
    },
    description: {
      en: 'Make sure that the image has a good resolution.',
      fi: 'Varmista, että kuva on riittävän selkeä.',
      cs: 'Ujistěte se, že obrázek má dobrou kvalitu.',
      no: 'Sørg for at bildet har god oppløsning.',
      ro: 'Asigurați-vă că fotografia are o rezoluție bună.',
      pl: 'Upewnij się, że zdjęcie ma dobrą rozdzielczość.',
      da: 'Sørg for, at billedet har en god opløsning.',
      sv: 'Se till att bilden har bra upplösning.',
      es: 'Asegúrese de que la imagen tenga una buena resolución.',
      pt: 'Certifique-se de que a imagem tenha uma boa resolução.',
      fr: "Assurez-vous que l'image ait une résolution suffisante.",
      de: 'Achten Sie darauf, dass das Bild eine gute Auflösung hat.',
      nl: 'Zorg ervoor dat de afbeelding een goede resolutie heeft.',
      it: "Assicurati che l'immagine abbia una buona risoluzione.",
    },
  },
  [ComplianceIssue.BLURRINESS]: {
    title: {
      en: 'Too blurry',
      fi: 'Liian epätarkka',
      cs: 'Příliš rozmazané',
      no: 'For uskarpt',
      ro: 'Prea estompată',
      pl: 'Zbyt rozmyte',
      da: 'For sløret',
      sv: 'För suddig',
      es: 'Demasiado borroso',
      pt: 'Muito embaçado',
      fr: 'Trop floue',
      de: 'Zu unscharf',
      nl: 'Te vaag',
      it: 'Troppo sfocata',
    },
    description: {
      en: 'Make sure that the image is not blurry.',
      fi: 'Varmista, että kuva ei ole epätarkka.',
      cs: 'Ujistěte se, že obrázek není rozmazaný.',
      no: 'Sørg for at bildet ikke er uskarpt.',
      ro: 'Asigurați-vă că fotografia nu este estompată.',
      pl: 'Upewnij się, że zdjęcie nie jest rozmyte.',
      da: 'Sørg for, at billedet ikke er sløret.',
      sv: 'Se till att bilden inte är suddig.',
      es: 'Asegúrese de que la imagen no esté borrosa.',
      pt: 'Certifique-se de que a imagem não esteja borrada.',
      fr: "Assurez-vous que l'image ne soit pas trop floue.",
      de: 'Achten Sie darauf, dass das Bild nicht unscharf ist.',
      nl: 'Zorg ervoor dat de afbeelding niet vaag is.',
      it: "Assicurati che l'immagine non sia sfocata.",
    },
  },
  [ComplianceIssue.UNDEREXPOSURE]: {
    title: {
      en: 'Underexposed',
      fi: 'Liian vähäinen valo',
      cs: 'Podexponováno',
      no: 'For lite lys',
      ro: 'Subexpus',
      pl: 'Niedoświetlone',
      da: 'Undereksponeret',
      sv: 'Underbelyst',
      es: 'Subexpuesta',
      pt: 'Subexposta',
      fr: 'Sousexposition',
      de: 'Unterbelichtet',
      nl: 'Onderbelicht',
      it: 'Sottoesposto',
    },
    description: {
      en: "Make sure that the image isn't too dark.",
      fi: 'Varmista, että kuva ei ole liian tumma.',
      cs: 'Ujistěte se, že obrázek není příliš tmavý.',
      no: 'Sørg for at bildet ikke er for mørkt.',
      ro: 'Asigurați-vă că imaginea nu este prea întunecată.',
      pl: 'Upewnij się, że obraz nie jest zbyt ciemny.',
      da: 'Sørg for, at billedet ikke er for mørkt.',
      sv: 'Se till att bilden inte är för mörk.',
      es: 'Asegúrese de que la imagen no esté demasiado oscura.',
      pt: 'Certifique-se de que a imagem não esteja muito escura.',
      fr: "Assurez-vous que l'image ne soit pas trop sombre.",
      de: 'Achten Sie darauf, dass das Bild nicht zu dunkel ist.',
      nl: 'Zorg ervoor dat de afbeelding niet te donker is.',
      it: "Assicurati che l'immagine non sia troppo scura.",
    },
  },
  [ComplianceIssue.OVEREXPOSURE]: {
    title: {
      en: 'Overexposed',
      fi: 'Liian valoisa',
      cs: 'Přeexponováno',
      no: 'Overeksponert',
      ro: 'Supraexpus',
      pl: 'Prześwietlone',
      da: 'Overeksponeret',
      sv: 'Överbelyst',
      es: 'Sobreexpuesta',
      pt: 'Superexposta',
      fr: 'Surexposition',
      de: 'Überbelichtet',
      nl: 'Overbelicht',
      it: 'Sovraesposto',
    },
    description: {
      en: "Make sure that the image isn't too bright.",
      fi: 'Varmista, ettei kuva ole liian kirkas.',
      cs: 'Ujistěte se, že obrázek není příliš jasný.',
      no: 'Sørg for at bildet ikke er for lyst.',
      ro: 'Asigurați-vă că imaginea nu este prea luminoasă.',
      pl: 'Upewnij się, że obraz nie jest zbyt jasny.',
      da: 'Sørg for, at billedet ikke er for lyst.',
      sv: 'Se till att bilden inte är för ljus.',
      es: 'Asegúrese de que la imagen no esté demasiado brillante.',
      pt: 'Certifique-se de que a imagem não esteja muito clara.',
      fr: "Assurez-vous que l'image ne soit pas trop lumineuse.",
      de: 'Achten Sie darauf, dass das Bild nicht zu hell ist.',
      nl: 'Zorg ervoor dat de afbeelding niet te licht is.',
      it: "Assicurati che l'immagine non sia troppo luminosa.",
    },
  },
  [ComplianceIssue.LENS_FLARE]: {
    title: {
      en: 'Lens flares',
      fi: 'Valoheijastukset',
      cs: 'Odlesky',
      no: 'Lysrefleksjoner',
      ro: 'Reflexii ale lentilei',
      pl: 'Odbicia światła na obiektywie',
      da: 'Linselys',
      sv: 'Linsblänk',
      es: 'Destellos de lente',
      pt: 'Reflexos da lente',
      fr: 'Reflets',
      de: 'Objektivreflexe',
      nl: 'Lensflares',
      it: 'Riflessi',
    },
    description: {
      en: "Make sure that there aren't any bright flare on the image.",
      fi: 'Varmista, ettei kuvassa ole kirkkaita heijastuksia.',
      cs: 'Ujistěte se, že na obrázku nejsou žádné jasné odlesky.',
      no: 'Sørg for at det ikke er noen sterkt reflekterende områder på bildet.',
      ro: 'Asigurați-vă că nu există reflexii puternice în imagine.',
      pl: 'Upewnij się, że na zdjęciu nie widać żadnych jasnych refleksów.',
      da: 'Sørg for, at der ikke er kraftige refleksioner på billedet.',
      sv: 'Se till att det inte finns några starka ljusreflexer på bilden.',
      es: 'Asegúrese de que no haya destellos brillantes en la imagen.',
      pt: 'Certifique-se de que não há reflexos fortes na imagem.',
      fr: "Assurez-vous que l'image ne contienne pas de reflets.",
      de: 'Vergewissern Sie sich, dass keine hellen Streulichter auf dem Bild zu sehen sind.',
      nl: 'Zorg ervoor dat er geen heldere flares op de afbeelding staan.',
      it: "Assicurati che non ci siano riflessi luminosi sull'immagine.",
    },
  },
  [ComplianceIssue.DIRTINESS]: {
    title: {
      en: 'Vehicle dirty',
      fi: 'Ajoneuvo likainen',
      cs: 'Znečištěné vozidlo',
      no: 'Skittent kjøretøy',
      ro: 'Vehicul murdar',
      pl: 'Pojazd jest brudny.',
      da: 'Køretøjet er beskidt',
      sv: 'Fordonet är smutsigt',
      es: 'Vehículo sucio',
      pt: 'Veículo sujo',
      fr: 'Véhicule sale',
      de: 'Fahrzeug verschmutzt',
      nl: 'Vervuilde auto',
      it: 'Veicolo sporco',
    },
    description: {
      en: 'Make sure that your vehicle is clean.',
      fi: 'Varmista, että ajoneuvo on puhdas.',
      cs: 'Ujistěte se, že váš vůz je čistý.',
      no: 'Sørg for at kjøretøyet er rent',
      ro: 'Asigurați-vă că vehiculul dumneavoastră este curat.',
      pl: 'Upewnij się, że twój pojazd jest czysty.',
      da: 'Sørg for, at dit køretøj er rent.',
      sv: 'Se till att ditt fordon är rent.',
      es: 'Asegúrese de que su vehículo esté limpio.',
      pt: 'Certifique-se de que o seu veículo esteja limpo.',
      fr: 'Assurez-vous que le véhicule soit propre.',
      de: 'Stellen Sie sicher, dass Ihr Fahrzeug sauber ist.',
      nl: 'Zorg ervoor dat uw voertuig schoon is.',
      it: 'Assicurati che il tuo veicolo sia pulito.',
    },
  },
  [ComplianceIssue.SNOWNESS]: {
    title: {
      en: 'Vehicle snowy',
      fi: 'Ajoneuvo lumessa',
      cs: 'Zasněžené vozidlo',
      no: 'Kjøretøy med snø',
      ro: 'Vehicul înzăpezit',
      pl: 'Pojazd na śniegu',
      da: 'Snefyldt køretøj',
      sv: 'Snöigt fordon',
      es: 'Vehículo con nieve',
      pt: 'Veículo com neve',
      fr: 'Véhicule enneigé',
      de: 'Fahrzeug verschneit',
      nl: 'Besneeuwde auto',
      it: 'Veicolo innevato',
    },
    description: {
      en: "Make sure that there aren't any snow on the vehicle.",
      fi: 'Varmista, ettei ajoneuvolla ole lunta.',
      cs: 'Ujistěte se, že na vozidlo neleží žádný sníh.',
      no: 'Sørg for at det ikke er snø på kjøretøyet.',
      ro: 'Asigurați-vă că nu există zăpadă pe vehicul.',
      pl: 'Upewnij się, że na pojeździe nie ma śniegu.',
      da: 'Sørg for, at der ikke er sne på køretøjet.',
      sv: 'Se till att det inte finns någon snö på fordonet.',
      es: 'Asegúrese de que no haya nieve en el vehículo.',
      pt: 'Certifique-se de que não há neve no veículo.',
      fr: "Assurez-vous qu'il n'y ait pas de neige sur le véhicule.",
      de: 'Vergewissern Sie sich, dass sich kein Schnee auf dem Fahrzeug befindet.',
      nl: 'Zorg ervoor dat er geen sneeuw op het voertuig zit.',
      it: 'Assicurati che non ci sia neve sul veicolo.',
    },
  },
  [ComplianceIssue.WETNESS]: {
    title: {
      en: 'Vehicle wet',
      fi: 'Ajoneuvo märkä',
      cs: 'Mokré vozidlo',
      no: 'Vått kjøretøy',
      ro: 'Vehicul umed',
      pl: 'Pojazd mokry',
      da: 'Køretøj vådt',
      sv: 'Fordonet är vått',
      es: 'Vehículo mojado',
      pt: 'Veículo molhado',
      fr: 'Véhicule mouillé',
      de: 'Fahrzeug nass',
      nl: 'Natte auto',
      it: 'Veicolo bagnato',
    },
    description: {
      en: 'Make sure that the vehicle is dry.',
      fi: 'Varmista, että ajoneuvo on kuiva.',
      cs: 'Ujistěte se, že vozidlo je suché.',
      no: 'Sørg for at kjøretøyet er tørt',
      ro: 'Asigurați-vă că vehiculul este uscat.',
      pl: 'Upewnij się, że pojazd jest suchy.',
      da: 'Sørg for, at køretøjet er tørt.',
      sv: 'Se till att fordonet är torrt.',
      es: 'Asegúrese de que el vehículo esté seco.',
      pt: 'Certifique-se de que o veículo esteja seco.',
      fr: "Assurez-vous qu'il n'y ait pas d'eau sur le véhicule.",
      de: 'Stellen Sie sicher, dass das Fahrzeug trocken ist.',
      nl: 'Zorg ervoor dat het voertuig droog is.',
      it: 'Assicurati che il veicolo sia asciutto.',
    },
  },
  [ComplianceIssue.REFLECTIONS]: {
    title: {
      en: 'Reflections',
      fi: 'Heijastukset',
      cs: 'Odraz',
      no: 'Refleksjoner',
      ro: 'Reflecții',
      pl: 'Odbicia',
      da: 'Refleksioner',
      sv: 'Reflektioner',
      es: 'Reflejos',
      pt: 'Reflexos',
      fr: 'Reflets',
      de: 'Reflexionen',
      nl: 'Reflecties',
      it: 'Riflessi',
    },
    description: {
      en: "Make sure that there are'nt any reflections on the vehicle.",
      fi: 'Varmista, ettei ajoneuvolla ole heijastuksia.',
      cs: 'Ujistěte se, že na vozidlo nepadají žádné odlesky.',
      no: 'Sørg for at det ikke er noen refleksjoner på kjøretøyet.',
      ro: 'Asigurați-vă că nu există reflexii pe vehicul.',
      pl: 'Upewnij się, że na pojeździe nie ma żadnych odbić.',
      da: 'Sørg for, at der ikke er nogen refleksioner på køretøjet.',
      sv: 'Se till att det inte finns några reflektioner på fordonet.',
      es: 'Asegúrese de que no haya reflejos en el vehículo.',
      pt: 'Certifique-se de que não há reflexos no veículo.',
      fr: "Assurez-vous qu'il n'y ait pas de reflets sur le véhicule.",
      de: 'Vergewissern Sie sich, dass es keine Reflexionen auf dem Fahrzeug gibt.',
      nl: 'Zorg ervoor dat er geen reflecties op het voertuig zijn.',
      it: 'Assicurati che non ci siano riflessi sul veicolo.',
    },
  },
  [ComplianceIssue.UNKNOWN_SIGHT]: {
    title: {
      en: 'Unknown sight',
      fi: 'Ei tunnistettu',
      cs: 'Neznámý úhel pohledu',
      no: 'Ukjent synsvinkel',
      ro: 'Imagine necunoscută',
      pl: 'Nieznany widok',
      da: 'Ukendt syn',
      sv: 'Okänt område',
      es: 'Vista desconocida',
      pt: 'Visão desconhecida',
      fr: 'Unknown sight',
      de: 'Unknown sight',
      nl: 'Unknown sight',
      it: 'Unknown sight',
    },
    description: {
      en: 'Internal Error : Unknown sight.',
      fi: 'Sisäinen virhe: Näkö ei tunnistettu.',
      cs: 'Chyba: Není možné určit pozici.',
      no: 'Intern feil: Ukjent synsvinkel.',
      ro: 'Eroare internă: Vedere necunoscută.',
      pl: 'Błąd wewnętrzny: nieznany widok.',
      da: 'Intern fejl: Ukendt syn.',
      sv: 'Internt fel: Okänt synfält.',
      es: 'Error interno: vista desconocida.',
      pt: 'Erro interno: vista desconhecida.',
      fr: 'Erreur interne : Unknown sight.',
      de: 'Interner Fehler : Unknown sight.',
      nl: 'Interne fout: Unknown sight.',
      it: 'Errore interno: Unknown sight.',
    },
  },
  [ComplianceIssue.UNKNOWN_VIEWPOINT]: {
    title: {
      en: 'Unknown viewpoint',
      fi: 'Epäselvä näkökulma',
      cs: 'Neznámý úhel pohledu',
      no: 'Ukjent synsvinkel',
      ro: 'Perspectivă necunoscută',
      pl: 'Nieznany punkt widzenia',
      da: 'Ukendt synsvinkel',
      sv: 'Okänt perspektiv',
      es: 'Punto de vista desconocido',
      pt: 'Ponto de vista desconhecido',
      fr: 'Unknown viewpoint',
      de: 'Unknown viewpoint',
      nl: 'Unknown viewpoint',
      it: 'Unknown viewpoint',
    },
    description: {
      en: 'Internal Error : Unknown viewpoint.',
      fi: 'Sisäinen virhe: Tuntematon näkökulma.',
      cs: 'Chyba: Neznámý úhel pohledu.',
      no: 'Intern feil: Ukjent synsvinkel.',
      ro: 'Eroare internă: punct de vedere necunoscut.',
      pl: 'Błąd wewnętrzny: nieznany punkt widzenia.',
      da: 'Intern fejl: Ukendt synsvinkel.',
      sv: 'Internt fel: Okänt perspektiv.',
      es: 'Error interno: punto de vista desconocido.',
      pt: 'Erro interno: Ponto de vista desconhecido.',
      fr: 'Erreur interne : Unknown viewpoint.',
      de: 'Interner Fehler : Unknown viewpoint.',
      nl: 'Interne fout: Unknown viewpoint.',
      it: 'Errore interno: Unknown viewpoint.',
    },
  },
  [ComplianceIssue.NO_VEHICLE]: {
    title: {
      en: 'No vehicle',
      fi: 'Ei ajoneuvoa',
      cs: 'Žádné vozidlo',
      no: 'Ingen kjøretøy',
      ro: 'Fără vehicul',
      pl: 'Brak pojazdu',
      da: 'Intet køretøj',
      sv: 'Inget fordon',
      es: 'No hay vehículo',
      pt: 'Nenhum veículo',
      fr: 'Pas de véhicule',
      de: 'Kein Fahrzeug',
      nl: 'Geen voertuig',
      it: 'Nessun veicolo',
    },
    description: {
      en: 'Make sure that there is a vehicle in the photo.',
      fi: 'Varmista, että kuvassa on auto.',
      cs: 'Ujistěte se, že na obrázku je vozidlo.',
      no: 'Sørg for at det er et kjøretøy på bildet.',
      ro: 'Asigurați-vă că în fotografie se află un vehicul.',
      pl: 'Upewnij się, że na zdjęciu znajduje się pojazd.',
      da: 'Sørg for, at der er et køretøj på billedet.',
      sv: 'Se till att det finns ett fordon på bilden.',
      es: 'Asegúrese de que haya un vehículo en la foto.',
      pt: 'Certifique-se de que há um veículo na foto.',
      fr: "Assurez-vous qu'il y ait un véhicule sur l'image",
      de: 'Vergewissern Sie sich, dass ein Fahrzeug auf dem Foto zu sehen ist.',
      nl: 'Zorg ervoor dat er een voertuig op de foto staat.',
      it: 'Assicurati che ci sia un veicolo nella foto.',
    },
  },
  [ComplianceIssue.WRONG_ANGLE]: {
    title: {
      en: 'Wrong angle',
      fi: 'Väärä kulma',
      cs: 'Špatný úhel',
      no: 'Feil vinkel',
      ro: 'Unghi incorect',
      pl: 'Nieprawidłowy kąt',
      da: 'Forkert vinkel',
      sv: 'Fel vinkel',
      es: 'Ángulo incorrecto',
      pt: 'Ângulo incorreto',
      fr: 'Mauvaise angle',
      de: 'Falscher Winkel',
      nl: 'Verkeerde hoek',
      it: 'Angolo sbagliato',
    },
    description: {
      en: 'Make sure to properly align the vehicle with the guides.',
      fi: 'Varmista, että ajoneuvo on oikein kohdistettu ohjaimiin.',
      cs: 'Ujistěte se, že vozidlo správně zarovnáte s vodítky.',
      no: 'Sørg for å justere kjøretøyet riktig med veilederne.',
      ro: 'Asigurați-vă că aliniați corect vehiculul cu ajutorul ghidajelor.',
      pl: 'Upewnij się, że pojazd jest prawidłowo ustawiony względem prowadnic.',
      da: 'Sørg for at justere køretøjet korrekt i forhold til markeringerne.',
      sv: 'Se till att fordonet är korrekt justerat i förhållande till markeringarna.',
      es: 'Asegúrese de alinear correctamente el vehículo con las guías.',
      pt: 'Certifique-se de alinhar corretamente o veículo com as guias.',
      fr: 'Assurez-vous de bien aligner le véhicule avec les guides.',
      de: 'Achten Sie darauf, dass das Fahrzeug richtig an den Führungen ausgerichtet ist.',
      nl: 'Zorg ervoor dat het voertuig goed is uitgelijnd met de gidsen.',
      it: 'Assicurati di allineare correttamente il veicolo con le guide.',
    },
  },
  [ComplianceIssue.WRONG_CENTER_PART]: {
    title: {
      en: 'Wrong center part',
      fi: 'Väärä keskiosa',
      cs: 'Nesprávná střední část',
      no: 'Feil del',
      ro: 'Partea centrală incorectă',
      pl: 'Nieprawidłowa część środkowa',
      da: 'Forkert midterdel',
      sv: 'Felaktig mittdel',
      es: 'Parte central incorrecta',
      pt: 'Peça central incorreta',
      fr: 'Mauvais centrage',
      de: 'Falsches Mittelteil',
      nl: 'Verkeerd middenstuk',
      it: 'Parte centrale sbagliata',
    },
    description: {
      en: 'Make sure to properly align the vehicle with the guides.',
      fi: 'Varmista, että ajoneuvo on oikein kohdistettu ohjaimiin.',
      cs: 'Ujistěte se, že vozidlo správně zarovnáte s vodítky.',
      no: 'Sørg for å justere kjøretøyet riktig med veilederne.',
      ro: 'Asigurați-vă că aliniați corect vehiculul cu ajutorul ghidajelor.',
      pl: 'Upewnij się, że pojazd jest prawidłowo ustawiony względem prowadnic.',
      da: 'Sørg for at justere køretøjet korrekt i forhold til markeringerne.',
      sv: 'Se till att fordonet är korrekt justerat i förhållande till markeringarna.',
      es: 'Asegúrese de alinear correctamente el vehículo con las guías.',
      pt: 'Certifique-se de alinhar corretamente o veículo com as guias.',
      fr: 'Assurez-vous de bien aligner le véhicule avec les guides.',
      de: 'Achten Sie darauf, dass das Fahrzeug richtig an den Führungen ausgerichtet ist.',
      nl: 'Zorg ervoor dat het voertuig goed is uitgelijnd met de gidsen.',
      it: 'Assicurati di allineare correttamente il veicolo con le guide.',
    },
  },
  [ComplianceIssue.MISSING_PARTS]: {
    title: {
      en: 'Missing parts',
      fi: 'Puuttuvat osat',
      cs: 'Chybějící díly',
      no: 'Manglende deler',
      ro: 'Piese lipsă',
      pl: 'Brakujące części',
      da: 'Manglende dele',
      sv: 'Saknade delar',
      es: 'Piezas faltantes',
      pt: 'Peças ausentes',
      fr: 'Parties manquantes',
      de: 'Fehlende Teile',
      nl: 'Ontbrekende onderdelen',
      it: 'Parti mancanti',
    },
    description: {
      en: 'Make sure to properly align the vehicle with the guides.',
      fi: 'Varmista, että ajoneuvo on oikein kohdistettu ohjaimiin.',
      cs: 'Ujistěte se, že vozidlo správně zarovnáte s vodítky.',
      no: 'Sørg for å justere kjøretøyet riktig i forhold til retningslinjene.',
      ro: 'Asigurați-vă că aliniați corect vehiculul cu ajutorul ghidajelor.',
      pl: 'Upewnij się, że pojazd jest prawidłowo ustawiony względem prowadnic.',
      da: 'Sørg for at justere køretøjet korrekt i forhold til markeringerne.',
      sv: 'Se till att fordonet är korrekt justerat i förhållande till markeringarna.',
      es: 'Asegúrese de alinear correctamente el vehículo con las guías.',
      pt: 'Certifique-se de alinhar o veículo corretamente com as guias.',
      fr: 'Assurez-vous de bien aligner le véhicule avec les guides.',
      de: 'Achten Sie darauf, dass das Fahrzeug richtig an den Führungen ausgerichtet ist.',
      nl: 'Zorg ervoor dat het voertuig goed is uitgelijnd met de gidsen.',
      it: 'Assicurati di allineare correttamente il veicolo con le guide.',
    },
  },
  [ComplianceIssue.HIDDEN_PARTS]: {
    title: {
      en: 'Hidden parts',
      fi: 'Piilotetut osat',
      cs: 'Skryté části',
      no: 'Skjulte deler',
      ro: 'Piese ascunse',
      pl: 'Ukryte elementy',
      da: 'Skjulte dele',
      sv: 'Dolda delar',
      es: 'Partes ocultas',
      pt: 'Peças ocultas',
      fr: 'Parties masquées',
      de: 'Versteckte Teile',
      nl: 'Verborgen onderdelen',
      it: 'Parti nascoste',
    },
    description: {
      en: 'Make sure that nothing is obstructing the vehicule from the camera.',
      fi: 'Varmista, ettei mitään estä kameran näkymää.',
      cs: 'Ujistěte se, že nic nebrání pohledu kamery na vozidlo.',
      no: 'Sørg for at ingenting hindrer kameraet i å se bilen.',
      ro: 'Asigurați-vă că nimic nu blochează vederea vehiculului din partea camerei.',
      pl: 'Upewnij się, że nic nie zasłania widoku pojazdu w kadrze.',
      da: 'Sørg for, at intet blokerer for kameraets udsyn til køretøjet.',
      sv: 'Se till att ingenting blockerar kamerans sikt över fordonet.',
      es: 'Asegúrese de que nada obstruya la vista del vehículo desde la cámara.',
      pt: 'Certifique-se de que nada está obstruindo a visão do veículo na câmera.',
      fr: 'Assurez-vous que rien ne masque le véhicule.',
      de: 'Vergewissern Sie sich, dass das Fahrzeug nicht von der Kamera verdeckt wird.',
      nl: 'Zorg ervoor dat er niets tussen het voertuig en de camera zit.',
      it: 'Assicurati che nulla ostruisca il veicolo dalla fotocamera.',
    },
  },
  [ComplianceIssue.TOO_ZOOMED]: {
    title: {
      en: 'Too close',
      fi: 'Liian lähellä',
      cs: 'Příliš blízko',
      no: 'For nært',
      ro: 'Prea aproape',
      pl: 'Zbyt blisko',
      da: 'For tæt på',
      sv: 'För nära',
      es: 'Demasiado cerca',
      pt: 'Muito perto',
      fr: 'Trop près',
      de: 'Zu nah',
      nl: 'Te dichtbij',
      it: 'Troppo vicino',
    },
    description: {
      en: 'Make sure to stand far enough from the vehicle.',
      fi: 'Pidä riittävä etäisyys ajoneuvosta.',
      cs: 'Ujistěte se, že jste dostatečně daleko od vozidla.',
      no: 'Sørg for å stå langt nok unna kjøretøyet.',
      ro: 'Asigurați-vă că vă aflați la o distanță suficientă de vehicul.',
      pl: 'Upewnij się, że stoisz w odpowiedniej odległości od pojazdu.',
      da: 'Sørg for at stå langt nok væk fra køretøjet.',
      sv: 'Se till att du står tillräckligt långt bort från fordonet.',
      es: 'Asegúrese de colocarse a una distancia suficiente del vehículo.',
      pt: 'Certifique-se de que está a uma distância segura do veículo.',
      fr: 'Assurez-vous de vous tenir assez loin du véhicule.',
      de: 'Achten Sie darauf, dass Sie weit genug vom Fahrzeug entfernt stehen.',
      nl: 'Zorg ervoor dat je voldoende afstand houdt van het voertuig.',
      it: 'Assicurati di stare abbastanza lontano dal veicolo.',
    },
  },
  [ComplianceIssue.NOT_ZOOMED_ENOUGH]: {
    title: {
      en: 'Too far',
      fi: 'Liian kaukana',
      cs: 'Příliš daleko',
      no: 'For langt',
      ro: 'Prea departe',
      pl: 'Zbyt daleko',
      da: 'For langt',
      sv: 'För långt bort',
      es: 'Demasiado lejos',
      pt: 'Muito longe',
      fr: 'Trop loin',
      de: 'Zu weit',
      nl: 'Te ver weg',
      it: 'Troppo lontano',
    },
    description: {
      en: 'Make sure to stand close enough from the vehicle.',
      fi: 'Varmista, että olet tarpeeksi lähellä ajoneuvoa.',
      cs: 'Ujistěte se, že jste dostatečně blízko vozidlu.',
      no: 'Stå nær nok bilen',
      ro: 'Asigurați-vă că vă aflați la o distanță suficient de mică de vehicul.',
      pl: 'Upewnij się, że stoisz wystarczająco blisko pojazdu.',
      da: 'Sørg for at stå tæt nok på køretøjet.',
      sv: 'Se till att du står tillräckligt nära fordonet.',
      es: 'Asegúrese de colocarse lo suficientemente cerca del vehículo.',
      pt: 'Certifique-se de que está perto o suficiente do veículo.',
      fr: 'Assurez-vous de vous tenir assez proche du véhicule.',
      de: 'Achten Sie darauf, dass Sie nahe genug am Fahrzeug stehen.',
      nl: 'Zorg ervoor dat je dicht genoeg bij het voertuig staat.',
      it: 'Assicurati di stare abbastanza vicino al veicolo.',
    },
  },
  [ComplianceIssue.INTERIOR_NOT_SUPPORTED]: {
    title: {
      en: 'Interior not supported',
      fi: 'Sisätilan tarkastus ei ole tuettu',
      cs: 'Interiér není podporován',
      no: 'Ikke støttet',
      ro: 'Interiorul nu este acceptat.',
      pl: 'Wnętrze nieobsługiwane',
      da: 'Interiør understøttes ikke',
      sv: 'Interiör stöds inte',
      es: 'Interior no compatible',
      pt: 'Interior não suportado',
      fr: 'Interior not supported',
      de: 'Interior not supported',
      nl: 'Interior not supported',
      it: 'Interior not supported',
    },
    description: {
      en: 'Internal Error : Interior not supported.',
      fi: 'Sisäinen virhe: Sisätilat eivät ole tuetut.',
      cs: 'Chyba: Interiér není podporován.',
      no: 'Intern feil: Interiør støttes ikke.',
      ro: 'Eroare internă: interiorul nu este acceptat.',
      pl: 'Błąd wewnętrzny: Obsługa wnętrza niedostępna.',
      da: 'Intern fejl: Billeder af interiøret understøttes ikke.',
      sv: 'Internt fel: Bild av interiör stöds inte.',
      es: 'Error interno: el interior no es compatible.',
      pt: 'Erro interno: interior não suportado.',
      fr: 'Erreur interne : Interior not supported.',
      de: 'Interner Fehler : Interior not supported.',
      nl: 'Interne fout: Interior not supported.',
      it: 'Errore interno: Interior not supported.',
    },
  },
  [ComplianceIssue.MISSING]: {
    title: {
      en: 'Missing',
      fi: 'Puuttuva',
      cs: 'Chybí',
      no: 'Manglende',
      ro: 'Lipsă',
      pl: 'Brak',
      da: 'Mangler',
      sv: 'Saknas',
      es: 'Faltante',
      pt: 'Ausente',
      fr: 'Missing',
      de: 'Missing',
      nl: 'Missing',
      it: 'Missing',
    },
    description: {
      en: 'Internal Error : Missing.',
      fi: 'Sisäinen virhe: Tiedot puuttuvat.',
      cs: 'Chyba: Chybějící data',
      no: 'Intern feil: Mangler',
      ro: 'Eroare internă: lipsă.',
      pl: 'Błąd wewnętrzny: brak.',
      da: 'Intern fejl: Mangler.',
      sv: 'Internt fel: Saknas.',
      es: 'Error interno: faltante.',
      pt: 'Erro interno: ausente.',
      fr: 'Erreur interne : Missing.',
      de: 'Interner Fehler : Missing.',
      nl: 'Interne fout: Missing.',
      it: 'Errore interno: Missing.',
    },
  },
  [ComplianceIssue.LOW_QUALITY]: {
    title: {
      en: 'Low quality',
      fi: 'Huono kunto',
      cs: 'Špatná kvalita',
      no: 'Dårlig kvalitet',
      ro: 'Calitate scăzută',
      pl: 'Niska jakość',
      da: 'Lav kvalitet',
      sv: 'Låg kvalitet',
      es: 'Baja calidad',
      pt: 'Baixa qualidade',
      fr: 'Basse qualité',
      de: 'Geringe Qualität',
      nl: 'Lage kwaliteit',
      it: 'Bassa qualità',
    },
    description: {
      en: 'Make sure that the photo has a good quality.',
      fi: 'Varmista, että valokuva on selkeä ja hyvälaatuinen.',
      cs: 'Ujistěte se, že fotografie má dobrou kvalitu.',
      no: 'Sørg for at bildet har god kvalitet.',
      ro: 'Asigurați-vă că fotografia are o calitate bună.',
      pl: 'Upewnij się, że zdjęcie jest dobrej jakości.',
      da: 'Sørg for, at billedet har en god kvalitet.',
      sv: 'Se till att fotot har bra kvalitet.',
      es: 'Asegúrese de que la foto tenga buena calidad.',
      pt: 'Certifique-se de que a foto tenha boa qualidade.',
      fr: 'Assurez-vous de labonne qualité de la photo.',
      de: 'Achten Sie darauf, dass das Foto eine gute Qualität hat.',
      nl: 'Zorg ervoor dat de foto van goede kwaliteit is.',
      it: 'Assicurati che la foto sia di buona qualità.',
    },
  },
  [ComplianceIssue.PORTRAIT_IMAGE]: {
    title: {
      en: 'Portrait Image',
      fi: 'Kuva (portrityyppinen)',
      cs: 'Portrétní snímek',
      no: 'Bilde (portrett)',
      ro: 'Imagine portret',
      pl: 'Zdjęcie portretowe',
      da: 'Portrætbillede',
      sv: 'Porträttbild',
      es: 'Imagen de retrato',
      pt: 'Imagem de Retrato',
      fr: 'Image en mode portrait',
      de: 'Hochformatbild',
      nl: 'Portretfoto',
      it: 'Immagine verticale',
    },
    description: {
      en: 'The picture is in portrait mode, please take it in landscape',
      fi: 'Kuvassa on pystyasento, ota se vaakatasoon',
      cs: 'Obraz je v portrétním režimu, prosím, pořiďte jej v režimu krajiny.',
      no: 'Bildet er i portrettmodus, vennligst ta det i landskapsmodus',
      ro: 'Imaginea este în modul portret, vă rugăm să o fotografiați în modul peisaj.',
      pl: 'Zdjęcie zostało wykonane w trybie portretowym, proszę zrobić zdjęcie w trybie krajobrazowym.',
      da: 'Billedet er i portrættilstand, tag det i stedet i landskabstilstand.',
      sv: 'Bilden är i stående format, ta den i liggande format.',
      es: 'La foto está en modo vertical, por favor, tómela en modo horizontal.',
      pt: 'A foto está no modo retrato, tire-a no modo paisagem.',
      fr: 'L’image est en mode portrait, veuillez la prendre en mode paysage',
      de: 'Das Bild ist im Hochformat, bitte fotografieren Sie im Querformat',
      nl: 'De foto is in portretstand, neem deze alstublieft in landschapmodus',
      it: "L'immagine è in modalità verticale, scattala in orizzontale",
    },
  },
  [ComplianceIssue.CONTAIN_FORBIDDEN_PARTS]: {
    title: {
      en: 'Contain forbidden parts',
      fi: 'Sisältää kiellettyjä osia',
      cs: 'Obsahuje zakázané části',
      no: 'Inneholder forbudte deler',
      ro: 'Conține piese interzise',
      pl: 'Zawiera niedozwolone części',
      da: 'Indeholder forbudte dele',
      sv: 'Innehåller förbjudna delar',
      es: 'Contiene partes prohibidas',
      pt: 'Contém peças proibidas',
      fr: 'Contient des parties interdites',
      de: 'Enthält verbotene Teile',
      nl: 'Bevat verboden onderdelen',
      it: 'Contiene parti vietate',
    },
    description: {
      en: 'Make sure to properly align the vehicle with the guides.',
      fi: 'Varmista, että ajoneuvo on oikein kohdistettu ohjaimiin.',
      cs: 'Ujistěte se, že vozidlo správně zarovnáte s vodítky.',
      no: 'Sørg for å justere kjøretøyet riktig med veilederne.',
      ro: 'Asigurați-vă că aliniați corect vehiculul cu ajutorul ghidajelor.',
      pl: 'Upewnij się, że pojazd jest prawidłowo ustawiony względem prowadnic.',
      da: 'Sørg for at justere køretøjet korrekt i forhold til markeringerne.',
      sv: 'Se till att fordonet är korrekt justerat i förhållande till markeringarna.',
      es: 'Asegúrese de alinear correctamente el vehículo con las guías.',
      pt: 'Certifique-se de alinhar corretamente o veículo com as guias.',
      fr: 'Assurez-vous de bien aligner le véhicule avec les guides.',
      de: 'Achten Sie darauf, dass das Fahrzeug richtig an den Führungen ausgerichtet ist.',
      nl: 'Zorg ervoor dat het voertuig goed is uitgelijnd met de gidsen.',
      it: 'Assicurati di allineare correttamente il veicolo con le guide.',
    },
  },
  [ComplianceIssue.VEHICLE_NOT_FULLY_IN_FRAME]: {
    title: {
      en: 'Vehicle not fully in frame',
      fi: 'Ajoneuvo ei ole kokonaan kuvassa',
      cs: 'Vozidlo není celé v záběru',
      no: 'Kjøretøyet er ikke helt i bildet',
      ro: 'Vehiculul nu este complet în cadru',
      pl: 'Pojazd nie jest w pełni widoczny w kadrze',
      da: 'Køretøjet er ikke helt i billedet',
      sv: 'Fordonet är inte helt i bild',
      es: 'El vehículo no está completamente en el encuadre',
      pt: 'Veículo não está totalmente no enquadramento',
      fr: 'Véhicule pas entièrement dans le cadre',
      de: 'Fahrzeug nicht vollständig im Bild',
      nl: 'Voertuig niet volledig in beeld',
      it: 'Veicolo non completamente inquadrato',
    },
    description: {
      en: 'Make sure that the entire vehicle is visible in the photo.',
      fi: 'Varmista, että koko ajoneuvo näkyy kuvassa.',
      cs: 'Ujistěte se, že celé vozidlo je viditelné na fotografii.',
      no: 'Sørg for at hele kjøretøyet er synlig på bildet.',
      ro: 'Asigurați-vă că întregul vehicul este vizibil în fotografie.',
      pl: 'Upewnij się, że cały pojazd jest widoczny na zdjęciu.',
      da: 'Sørg for, at hele køretøjet er synligt på billedet.',
      sv: 'Se till att hela fordonet syns på bilden.',
      es: 'Asegúrese de que el vehículo completo sea visible en la foto.',
      pt: 'Certifique-se de que o veículo inteiro esteja visível na foto.',
      fr: "Assurez-vous que l'intégralité du véhicule soit visible sur la photo.",
      de: 'Stellen Sie sicher, dass das gesamte Fahrzeug auf dem Foto sichtbar ist.',
      nl: 'Zorg ervoor dat het hele voertuig op de foto zichtbaar is.',
      it: "Assicurati che l'intero veicolo sia visibile nella foto.",
    },
  },
  [ComplianceIssue.FINGER_ON_VEHICLE]: {
    title: {
      en: 'Finger on lens',
      fi: 'Sormi objektiivin päällä',
      cs: 'Prst na objektivu',
      no: 'Finger på linsen',
      ro: 'Deget pe obiectiv',
      pl: 'Palec na obiektywie',
      da: 'Finger på linsen',
      sv: 'Finger på linsen',
      es: 'Dedo en la lente',
      pt: 'Dedo na lente',
      fr: "Doigt sur l'objectif",
      de: 'Finger auf dem Objektiv',
      nl: 'Vinger op de lens',
      it: "Dito sull'obiettivo",
    },
    description: {
      en: 'Make sure that there is no finger on the camera lens.',
      fi: 'Varmista, ettei kameran objektiivin päällä ole sormea.',
      cs: 'Ujistěte se, že na objektivu fotoaparátu není prst.',
      no: 'Sørg for at det ikke er noen finger på kameralinsen.',
      ro: 'Asigurați-vă că nu există un deget pe obiectivul camerei.',
      pl: 'Upewnij się, że na obiektywie aparatu nie ma palca.',
      da: 'Sørg for, at der ikke er en finger på kameralinsen.',
      sv: 'Se till att det inte finns ett finger på kameralinsen.',
      es: 'Asegúrese de que no haya un dedo en la lente de la cámara.',
      pt: 'Certifique-se de que não há nenhum dedo na lente da câmera.',
      fr: "Assurez-vous qu'il n'y ait pas de doigt sur l'objectif de l'appareil photo.",
      de: 'Stellen Sie sicher, dass kein Finger auf dem Kameraobjektiv liegt.',
      nl: 'Zorg ervoor dat er geen vinger op de cameralens zit.',
      it: "Assicurati che non ci sia un dito sull'obiettivo della fotocamera.",
    },
  },
};

/**
 * The translated labels for each viewpoint available in the SDK.
 */
export const viewpointLabels: Record<Viewpoint, TranslationObject> = {
  [Viewpoint.FRONT]: {
    en: 'Front',
    fi: 'Etupuoli',
    cs: 'Přední',
    no: 'Foran',
    fr: 'Avant',
    de: 'Vorne',
    nl: 'Voorkant',
    it: 'Anteriore',
    pt: 'Frente',
    es: 'Frontal',
    sv: 'Fram',
    da: 'Front',
    pl: 'Przód',
    ro: 'Față',
  },
  [Viewpoint.FRONT_LEFT]: {
    en: 'Front Left',
    fi: 'Etulasi, vasen',
    cs: 'Levý přední',
    no: 'Venstre foran',
    fr: 'Avant gauche',
    de: 'Vorne links',
    nl: 'Linksvoor',
    it: 'Anteriore sinistro',
    pt: 'Frente esquerda',
    es: 'Frontal izquierdo',
    sv: 'Fram vänster',
    da: 'Front venstre',
    pl: 'Przód lewy',
    ro: 'Față stânga',
  },
  [Viewpoint.LEFT]: {
    en: 'Left',
    fi: 'Vasemmalla',
    cs: 'Levý',
    no: 'Venstre',
    fr: 'Gauche',
    de: 'Links',
    nl: 'Linkerkant',
    it: 'Sinistro',
    pt: 'Esquerda',
    es: 'Izquierdo',
    sv: 'Vänster',
    da: 'Venstre',
    pl: 'Lewy',
    ro: 'Stânga',
  },
  [Viewpoint.BACK_LEFT]: {
    en: 'Rear Left',
    fi: 'Takaisin, vasen',
    cs: 'Zadní levý',
    no: 'Bakre venstre',
    fr: 'Arrière gauche',
    de: 'Hinten links',
    nl: 'Linksachter',
    it: 'Posteriore sinistro',
    pt: 'Traseira esquerda',
    es: 'Trasero izquierdo',
    sv: 'Bak vänster',
    da: 'Bag venstre',
    pl: 'Tył lewy',
    ro: 'Spate stânga',
  },
  [Viewpoint.BACK]: {
    en: 'Rear',
    fi: 'Takana',
    cs: 'Zadní',
    no: 'Bakre',
    fr: 'Arrière',
    de: 'Hinten',
    nl: 'Achterkant',
    it: 'Posteriore',
    pt: 'Traseira',
    es: 'Trasero',
    sv: 'Bak',
    da: 'Bag',
    pl: 'Tył',
    ro: 'Spate',
  },
  [Viewpoint.BACK_RIGHT]: {
    en: 'Rear Right',
    fi: 'Takainen oikea',
    cs: 'Zadní pravý',
    no: 'Bakre høyre',
    fr: 'Arrière droit',
    de: 'Hinten rechts',
    nl: 'Rechtsachter',
    it: 'Posteriore destro',
    pt: 'Traseira direita',
    es: 'Trasero derecho',
    sv: 'Bak höger',
    da: 'Bag højre',
    pl: 'Tył prawy',
    ro: 'Spate dreapta',
  },
  [Viewpoint.RIGHT]: {
    en: 'Right',
    fi: 'Oikein',
    cs: 'Pravý',
    no: 'Høyre',
    fr: 'Droite',
    de: 'Rechts',
    nl: 'Rechterkant',
    it: 'Destro',
    pt: 'Direita',
    es: 'Derecho',
    sv: 'Höger',
    da: 'Højre',
    pl: 'Prawy',
    ro: 'Dreapta',
  },
  [Viewpoint.FRONT_RIGHT]: {
    en: 'Front Right',
    fi: 'Etupuoli, oikea',
    cs: 'Přední pravý',
    no: 'Høyre foran',
    fr: 'Avant droit',
    de: 'Vorne rechts',
    nl: 'Rechtsvoor',
    it: 'Anteriore destro',
    pt: 'Frente direita',
    es: 'Frontal derecho',
    sv: 'Fram höger',
    da: 'Front højre',
    pl: 'Przód prawy',
    ro: 'Față dreapta',
  },
};
